/**
 * Study Mode Demo component - Claude Code style demo section with tabs
 */

import {useState} from 'react';
import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface DemoEnvironment {
  id: string;
  name: string;
  description: string;
  code: string;
  highlight?: string;
}

const environments: DemoEnvironment[] = [
  {
    id: 'web',
    name: 'Web',
    description: 'Work with Study Mode directly in your browser. Upload documents, chat with AI, and generate study materials all in one place.',
    code: `> Upload my biology lecture notes

• I'll analyze your biology notes and extract all key concepts, definitions, and important information.

• Analyze(what: "Extract key concepts, definitions, formulas, and important facts from the uploaded biology lecture notes. Organize them by topic and highlight connections between concepts.")

✓ Analysis complete
  - 15 key concepts identified
  - 8 formulas extracted
  - 3 main topics organized`,
    highlight: '* Welcome to Study Mode',
  },
  {
    id: 'mobile',
    name: 'Mobile',
    description: 'Study on the go with our mobile app. Take photos of notes, use voice commands, and access your study materials anywhere.',
    code: `> Take a photo of my math homework

📸 Photo captured
• Processing image...
• Extracting text and equations...

✓ Homework analyzed
  - 12 problems identified
  - Solutions generated
  - Step-by-step explanations ready

> Explain problem 3

• Problem 3: Quadratic equation
  x² + 5x + 6 = 0
  
  Solution:
  1. Factor: (x + 2)(x + 3) = 0
  2. Solve: x = -2 or x = -3`,
  },
  {
    id: 'extension',
    name: 'Browser Extension',
    description: 'Integrate Study Mode into your workflow. Highlight text on any webpage and get instant explanations or create flashcards.',
    code: `> Explain this concept

Selected text: "Photosynthesis is the process by which plants convert light energy into chemical energy..."

• Photosynthesis is the process plants use to convert light energy into chemical energy stored in glucose.

  Key points:
  - Occurs in chloroplasts
  - Requires: CO₂, H₂O, sunlight
  - Produces: glucose, O₂

> Create flashcard

✓ Flashcard created
  Q: What is photosynthesis?
  A: Process plants use to convert light energy into chemical energy`,
  },
];

export function StudyDemo() {
  const {ref, isVisible} = useScrollAnimation();
  const [activeEnv, setActiveEnv] = useState(environments[0].id);

  const activeDemo = environments.find((env) => env.id === activeEnv) || environments[0];

  return (
    <Section padding="lg" background="white" ref={ref} className="bg-background dark:bg-[#1E1E1E]">
      <Container>
        <div className={`transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Header with Tabs */}
          <div className="text-center mb-16">
            <p className="text-sm text-textSecondary dark:text-[#858585] mb-6">
              Use Study Mode where you work
            </p>
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-background dark:bg-[#1E1E1E] p-1">
              {environments.map((env) => (
                <button
                  key={env.id}
                  onClick={() => setActiveEnv(env.id)}
                    className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
                      activeEnv === env.id
                        ? 'bg-backgroundSecondary dark:bg-[#252526] border border-orange-500/40 text-textPrimary dark:text-[#CCCCCC] shadow-sm'
                        : 'text-textSecondary dark:text-[#858585] hover:text-textPrimary dark:hover:text-[#CCCCCC] hover:text-orange-600/70 dark:hover:text-orange-500/70'
                    }`}
                >
                  {env.name}
                </button>
              ))}
            </div>
          </div>

          {/* Main Demo Panel */}
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Description */}
              <div className="space-y-6">
                <h2 className="text-5xl sm:text-6xl font-bold text-textPrimary dark:text-[#CCCCCC] leading-tight">
                  {activeDemo.name}
                </h2>
                <p className="text-lg sm:text-xl text-textSecondary dark:text-[#858585] leading-relaxed">
                  {activeDemo.description}
                </p>
              </div>

              {/* Right: Terminal/Demo Window */}
              <div className="relative">
                {/* Orange Background Panel - Subtle gradient */}
                <div className="absolute -inset-4 bg-gradient-to-br from-orange-500/10 via-orange-500/5 to-transparent dark:from-orange-500/20 dark:via-orange-500/10 dark:to-transparent rounded-3xl blur-3xl"></div>
                
                {/* Terminal Window */}
                <div className="relative bg-white dark:bg-[#1E1E1E] rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden">
                  {/* Window Controls */}
                  <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-[#252526] border-b border-gray-200 dark:border-gray-700">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                  </div>

                  {/* Terminal Content */}
                  <div className="p-6 bg-white dark:bg-[#1E1E1E]">
                    {activeDemo.highlight && (
                      <div className="mb-4 px-4 py-2.5 bg-orange-500/10 dark:bg-orange-500/20 border-l-4 border-orange-500 rounded-r">
                        <span className="text-orange-600 dark:text-orange-400 font-medium text-sm">
                          {activeDemo.highlight}
                        </span>
                      </div>
                    )}
                    <div className="font-mono text-sm text-textPrimary dark:text-[#CCCCCC] leading-relaxed space-y-3">
                      {activeDemo.code.split('\n').map((line, index) => {
                        // Style different types of lines
                        if (line.startsWith('>')) {
                          return (
                            <div key={index} className="text-textPrimary dark:text-[#CCCCCC] font-medium">
                              <span className="text-orange-600/80 dark:text-orange-500/80">{'>'}</span>
                              {line.slice(1)}
                            </div>
                          );
                        } else if (line.startsWith('•')) {
                          return (
                            <div key={index} className="text-textSecondary dark:text-[#858585] ml-4">
                              {line.includes('Analyze(') || line.includes('Generate(') ? (
                                <>
                                  {line.split('(')[0]}(
                                  <span className="text-orange-600/70 dark:text-orange-500/70">{line.split('(')[1]}</span>
                                </>
                              ) : (
                                line
                              )}
                            </div>
                          );
                        } else if (line.startsWith('✓')) {
                          return (
                            <div key={index} className="text-green-600 dark:text-green-400 font-medium mt-4">
                              {line}
                            </div>
                          );
                        } else if (line.startsWith('  -') || line.startsWith('  ')) {
                          return (
                            <div key={index} className="text-textTertiary dark:text-[#6A6A6A] ml-6 text-xs">
                              {line.trim()}
                            </div>
                          );
                        } else if (line.trim() === '') {
                          return <div key={index} className="h-2"></div>;
                        } else {
                          return (
                            <div key={index} className="text-textSecondary dark:text-[#858585]">
                              {line}
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}

